/**
 * Phase B.4 — Lab Assistant billing UI state.
 *
 * Holds the latest balance snapshot (kept fresh by every Lab
 * Assistant response carrying `creditsRemaining`) and the
 * insufficient-credits modal state (opened by the RTK middleware
 * when any LA mutation rejects with 402 / 403 + a billing error
 * code).
 *
 * Pure UI state — no async work here. The `lab-assistant-billing.middleware`
 * does the listening and dispatches into this slice.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type InsufficientCreditsErrorCode =
  | 'INSUFFICIENT_CREDITS'
  | 'NO_LAB_ASSISTANT_CREDITS';

export interface LabAssistantBillingState {
  /**
   * The latest known balance. `null` until the user makes their
   * first LA call (or until a future `GET /me/entitlements` call
   * pre-populates it). UI should render a placeholder when null.
   */
  balance: { plan: number; purchased: number } | null;

  /**
   * Caller's tier `costMultiplier`. Surfaced for upgrade-upsell
   * copy ("you're paying 3×; Standard would drop to 2.5×").
   */
  costMultiplier: number | null;

  /**
   * ISO timestamp of the last balance update. UI shows a subtle
   * "updated Xs ago" tooltip in the balance widget.
   */
  lastUpdatedAt: string | null;

  /** Insufficient-credits modal state. */
  insufficientCreditsModal: {
    open: boolean;
    code?: InsufficientCreditsErrorCode;
    /** Plan name from the error payload, e.g. "No subscription". */
    planName?: string;
    /** Operation that triggered the modal (e.g. "metaprompt"). */
    operation?: string;
    /** Raw message — fallback copy when we don't have a friendlier string. */
    message?: string;
  };
}

const initialState: LabAssistantBillingState = {
  balance: null,
  costMultiplier: null,
  lastUpdatedAt: null,
  insufficientCreditsModal: { open: false },
};

const slice = createSlice({
  name: 'labAssistantBilling',
  initialState,
  reducers: {
    /**
     * Called by the middleware after a successful LA mutation
     * carrying balance fields. No-op when the response doesn't
     * include them (internal callers / super-admin / Prompt
     * Studio paths).
     */
    updateFromResponse(
      state,
      action: PayloadAction<{
        creditsRemaining?: { plan: number; purchased: number };
        creditMultiplier?: number;
      }>,
    ) {
      const { creditsRemaining, creditMultiplier } = action.payload;
      if (creditsRemaining) {
        state.balance = creditsRemaining;
        state.lastUpdatedAt = new Date().toISOString();
      }
      if (typeof creditMultiplier === 'number' && creditMultiplier > 0) {
        state.costMultiplier = creditMultiplier;
      }
    },

    /** Manual seed (e.g. after a successful login, from an entitlements query). */
    setBalance(
      state,
      action: PayloadAction<{
        balance: { plan: number; purchased: number };
        costMultiplier?: number;
      }>,
    ) {
      state.balance = action.payload.balance;
      if (typeof action.payload.costMultiplier === 'number' && action.payload.costMultiplier > 0) {
        state.costMultiplier = action.payload.costMultiplier;
      }
      state.lastUpdatedAt = new Date().toISOString();
    },

    /** Open the 402 modal — usually dispatched from the RTK middleware. */
    openInsufficientCreditsModal(
      state,
      action: PayloadAction<{
        code: InsufficientCreditsErrorCode;
        planName?: string;
        operation?: string;
        message?: string;
      }>,
    ) {
      state.insufficientCreditsModal = {
        open: true,
        ...action.payload,
      };
    },

    /** Close the modal (X button / "Maybe later" CTA). */
    closeInsufficientCreditsModal(state) {
      state.insufficientCreditsModal = { open: false };
    },
  },
});

export const {
  updateFromResponse,
  setBalance,
  openInsufficientCreditsModal,
  closeInsufficientCreditsModal,
} = slice.actions;

export const labAssistantBillingReducer = slice.reducer;
export const labAssistantBillingSliceName = slice.name;
